import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, FloppyDisk, CircleNotch, Bed, Users, Info, Baby, ArrowsClockwise, Tag, WarningCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import RatePlansManager from "@/components/RatePlansManager";

const EMPTY = {
  property_id: "",
  name: "",
  description: "",
  adult_spaces: 2,
  children_spaces: 0,
  cot_spaces: 0,
  max_occupancy: 2,
  default_occupancy: 2,
  count_of_rooms: 1,
  currency: "EUR",
  color: "#0369a1",
  room_kind: "room",
  unit_names: [],
};

/**
 * Postavke → Tipovi smještaja (Room Types)
 * Kanal-nativna hijerarhija: Property → RoomType → Unit
 * - RoomType drži cijenu, kapacitet (Channex bed-space model) i Sinkronizacija.
 * - Units su pojedinačne bookable jedinice — samo identitet + housekeeping.
 */

/**
 * Extract a human-readable unit name from a localized `name` dict.
 * Order of preference: hr → en → any first value → "Unnamed".
 */
function _unitDisplayName(u) {
  const n = u?.name;
  if (!n) return "Unnamed";
  if (typeof n === "string") return n;
  return n.hr || n.en || Object.values(n)[0] || "Unnamed";
}

export default function RoomTypesPage() {
  const { activePropertyId } = useProperty();
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState(null);
  const [selectedProp, setSelectedProp] = useState("");
  const [editing, setEditing] = useState(null);
  const [originalCount, setOriginalCount] = useState(0);  // count_of_rooms at edit-open, used to know the delta
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [ratePlansFor, setRatePlansFor] = useState(null); // RoomType object or null
  const [existingUnits, setExistingUnits] = useState([]);  // Loaded when editing to allow inline rename

  const load = async () => {
    try {
      const [ps, rts] = await Promise.all([api.get("/properties"), api.get("/room-types")]);
      setProperties(ps.data || []);
      setRoomTypes(rts.data || []);
      if (!selectedProp && (ps.data || []).length > 0) setSelectedProp(ps.data[0].id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  // When the global switcher changes, reflect it in the local property filter.
  useEffect(() => {
    if (activePropertyId) setSelectedProp(activePropertyId);
  }, [activePropertyId]);

  const activeProperty = properties.find((p) => p.id === selectedProp);

  const openNew = () => {
    setForm({
      ...EMPTY,
      property_id: selectedProp || (properties[0]?.id || ""),
      unit_names: [""],
    });
    setOriginalCount(0);
    setEditing("new");
  };
  const openEdit = async (rt) => {
    // Map legacy fields → new names if the record predates the migration.
    setForm({
      ...EMPTY,
      ...rt,
      adult_spaces: rt.adult_spaces ?? rt.capacity_adults ?? 2,
      children_spaces: rt.children_spaces ?? rt.capacity_children ?? 0,
      cot_spaces: rt.cot_spaces ?? rt.capacity_infants ?? 0,
      max_occupancy: rt.max_occupancy ?? ((rt.adult_spaces ?? rt.capacity_adults ?? 2) + (rt.children_spaces ?? rt.capacity_children ?? 0)),
      default_occupancy: rt.default_occupancy ?? rt.capacity_adults ?? 2,
      unit_names: [],
    });
    setOriginalCount(rt.count_of_rooms || 1);
    setEditing(rt.id);
    // Load existing units so the user can rename them individually. Kept
    // separate from the RoomType form so the /room-types PUT payload stays lean.
    try {
      const { data } = await api.get("/units", { params: { room_type_id: rt.id } });
      setExistingUnits(data || []);
    } catch { setExistingUnits([]); }
  };
  const close = () => { setEditing(null); setForm(EMPTY); setOriginalCount(0); setExistingUnits([]); };
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.property_id) return toast.error("Odaberi objekt.");
    if (!form.name.trim()) return toast.error("Naziv tipa smještaja je obavezan.");
    if (Number(form.adult_spaces) < 1) return toast.error("Broj mjesta za odrasle mora biti barem 1.");
    const maxPrice = form.max_price === "" || form.max_price == null ? null : Number(form.max_price);
    if (maxPrice == null || maxPrice <= 0) return toast.error("Max cijena po noćenju je obavezna — zakonski je ovo gornja granica cijene za ovaj tip smještaja.");
    setSaving(true);
    try {
      if (editing === "new") {
        // Filter out empty unit names — user might create units manually later.
        const cleanNames = (form.unit_names || []).map((n) => (n || "").trim()).filter(Boolean);
        const payload = { ...form, max_price: maxPrice, unit_names: cleanNames.length ? cleanNames : null };
        await api.post("/room-types", payload);
        toast.success("Tip smještaja kreiran i sinkroniziran s OTA-ima.");
      } else {
        const { property_id, unit_names, ...updates } = form;
        void property_id;
        // If the user grew count_of_rooms, hand any typed names to the backend
        // as `additional_unit_names` — the backend will use them for the delta
        // units and auto-name the rest.
        const growth = Math.max(0, (Number(form.count_of_rooms) || 0) - originalCount);
        if (growth > 0) {
          const cleaned = (unit_names || []).slice(0, growth).map((n) => (n || "").trim());
          updates.additional_unit_names = cleaned.some(Boolean) ? cleaned : null;
        }
        await api.put(`/room-types/${editing}`, { ...updates, max_price: maxPrice });
        // Also persist any inline unit-name edits (only PUT units whose names changed)
        const originalById = new Map(existingUnits.map((u) => [u.id, _unitDisplayName(u)]));
        const renames = (form._unit_edits || [])
          .filter((e) => e && e.id && (e.name || "").trim() && (e.name || "").trim() !== originalById.get(e.id));
        if (renames.length > 0) {
          await Promise.all(renames.map((e) => api.put(`/units/${e.id}`, { name: { hr: e.name.trim() } })));
          toast.success(`${renames.length} naziv${renames.length === 1 ? "" : "a"} jedinice ažuriran${renames.length === 1 ? "" : "o"}.`);
        }
        toast.success("Tip smještaja ažuriran i sinkroniziran.");
      }
      close();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rt) => {
    if (!window.confirm(`Obrisati tip "${rt.name}"?\nOvo će obrisati sve pripadajuće smještajne jedinice i mapiranje na OTA-ima.`)) return;
    try {
      await api.delete(`/room-types/${rt.id}`);
      toast.success("Tip smještaja obrisan.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (roomTypes === null) return <div className="flex min-h-[50vh] items-center justify-center"><CircleNotch size={22} className="animate-spin text-primary" /></div>;

  const filtered = selectedProp ? roomTypes.filter((rt) => rt.property_id === selectedProp) : roomTypes;

  if (properties.length === 0) {
    return (
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Bed size={20} weight="duotone" className="text-primary" />
          <h1 className="text-xl font-semibold">Tipovi smještaja</h1>
        </div>
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center">
          <p className="text-sm font-medium">Prvo kreirajte objekt.</p>
          <p className="mt-1 text-xs text-muted-foreground">Tipovi smještaja pripadaju objektu. Idi na Objekti → Dodaj objekt.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6" data-testid="room-types-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="hidden font-heading text-2xl font-bold tracking-tight text-foreground sm:block">Tipovi smještaja</h1>
          <p className="hidden text-sm text-muted-foreground sm:mt-1 sm:block">
            Grupiraj identične smještajne jedinice u tipove. Sve promjene automatski se prenose na OTA-e.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {properties.length > 1 && !activePropertyId && (
            <select
              value={selectedProp}
              onChange={(e) => setSelectedProp(e.target.value)}
              className="rw-input"
              data-testid="rt-property-filter"
            >
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <SyncNowButton onDone={load} />
          <button onClick={openNew} className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90" data-testid="rt-new-btn">
            <Plus size={14} weight="bold" /> Novi tip
          </button>
        </div>
      </div>


      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center">
          <Bed size={28} weight="duotone" className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">Još nemate niti jedan tip smještaja u ovom objektu.</p>
          <button onClick={openNew} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
            <Plus size={14} weight="bold" /> Kreiraj prvi tip
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((rt) => {
            const a = rt.adult_spaces ?? rt.capacity_adults ?? 0;
            const c = rt.children_spaces ?? rt.capacity_children ?? 0;
            const cot = rt.cot_spaces ?? rt.capacity_infants ?? 0;
            return (
              <div key={rt.id} className="rounded-lg border border-border bg-card p-4" data-testid={`rt-card-${rt.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: rt.color || "#0369a1" }} />
                      <h3 className="truncate text-sm font-semibold">{rt.name}</h3>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {rt.count_of_rooms} × jedinica • {a} odrasl. + {c} dj. {cot > 0 ? `+ ${cot} beba` : ""}{rt.max_price ? ` • max €${Number(rt.max_price).toFixed(2)}/noć` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setRatePlansFor(rt)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" title="Rate Planovi" data-testid={`rt-rate-plans-${rt.id}`}><Tag size={14} /></button>
                    <button onClick={() => openEdit(rt)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`rt-edit-${rt.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => remove(rt)} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`rt-delete-${rt.id}`}><Trash size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users size={12} weight="duotone" /> Max {rt.max_occupancy ?? (a + c)}
                  </div>
                  <span className="text-[11px] text-muted-foreground">Cijene u Pricing</span>
                </div>
                {rt.channex_room_type_id && (
                  <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                    ✓ Sinkronizirano s OTA-ima
                  </div>
                )}
                {!rt.max_price && (
                  <button
                    onClick={() => openEdit(rt)}
                    className="mt-2 flex w-full items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-left text-[11px] font-medium text-amber-800 hover:bg-amber-100"
                    data-testid={`rt-max-price-missing-${rt.id}`}
                  >
                    <WarningCircle size={13} weight="fill" className="shrink-0" /> Max cijena nije postavljena — klikni da je dodaš
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <Editor form={form} setForm={setForm} onSave={save} onCancel={close} saving={saving} isNew={editing === "new"} originalCount={originalCount} properties={properties} activeProperty={activeProperty} existingUnits={existingUnits} />
      )}

      {ratePlansFor && (
        <RatePlansManager roomType={ratePlansFor} onClose={() => setRatePlansFor(null)} />
      )}
    </section>
  );
}

/**
 * Editor drawer/modal.
 * Layout is 2-column on desktop:
 *   left  = form fields (Channex bed-space model)
 *   right = live occupancy scenario preview (Booking.com-style)
 */
function Editor({ form, setForm, onSave, onCancel, saving, isNew, originalCount, properties, activeProperty, existingUnits = [] }) {
  // For new: user names all N units. For edit-grow: user names ONLY the delta (new - old) units.
  const growth = isNew ? Number(form.count_of_rooms || 0) : Math.max(0, Number(form.count_of_rooms || 0) - Number(originalCount || 0));
  const set = (k) => (e) => {
    const v = e?.target?.value ?? e;
    const num = ["adult_spaces", "children_spaces", "cot_spaces", "max_occupancy", "default_occupancy", "count_of_rooms"].includes(k);
    setForm((f) => {
      const next = { ...f, [k]: num ? Number(v) : v };
      // Auto-recalculate max_occupancy = adults + children whenever they change
      if (k === "adult_spaces" || k === "children_spaces") {
        next.max_occupancy = Number(next.adult_spaces || 0) + Number(next.children_spaces || 0);
        if (!next.default_occupancy || next.default_occupancy > next.max_occupancy) {
          next.default_occupancy = Math.min(next.default_occupancy || next.adult_spaces, next.max_occupancy);
        }
      }
      // Resize unit_names to match the # of NEW units (delta for edit, full count for new).
      if (k === "count_of_rooms" && Array.isArray(next.unit_names)) {
        const n = isNew ? (Number(v) || 0) : Math.max(0, (Number(v) || 0) - Number(originalCount || 0));
        const arr = [...next.unit_names];
        while (arr.length < n) arr.push("");
        next.unit_names = arr.slice(0, n);
      }
      return next;
    });
  };

  const setUnitName = (i) => (e) => {
    setForm((f) => {
      const arr = [...(f.unit_names || [])];
      arr[i] = e.target.value;
      return { ...f, unit_names: arr };
    });
  };

  const scenarios = useMemo(
    () => buildOccupancyScenarios({
      adult_spaces: Number(form.adult_spaces || 0),
      children_spaces: Number(form.children_spaces || 0),
      cot_spaces: Number(form.cot_spaces || 0),
      max_occupancy: Number(form.max_occupancy || 0),
    }),
    [form.adult_spaces, form.children_spaces, form.cot_spaces, form.max_occupancy]
  );

  const totalMax = Number(form.max_occupancy || 0);
  const childAgeMin = activeProperty?.child_age_min ?? 2;
  const childAgeMax = activeProperty?.child_age_max ?? 17;
  const infantAgeMax = activeProperty?.infant_age_max ?? 2;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onCancel} />
      <div className="fixed left-1/2 top-1/2 z-50 flex max-h-[90dvh] w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h4 className="text-base font-semibold">{isNew ? "Novi tip smještaja" : "Uredi tip"}</h4>
          <button onClick={onCancel} className="rounded-md p-1 hover:bg-muted"><X size={16} /></button>
        </div>
        <form onSubmit={onSave} noValidate className="flex flex-1 flex-col overflow-hidden">
          <div className="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-5">
            {/* --- Left: form --- */}
            <div className="space-y-4 lg:col-span-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {isNew && (
                  <Field label="Objekt *">
                    <select required value={form.property_id} onChange={set("property_id")} className="rw-input" data-testid="rt-form-property">
                      <option value="">— odaberi —</option>
                      {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="Naziv tipa *" hint="npr. 'Studio Deluxe', '1-sobni apartman'">
                  <input required value={form.name} onChange={set("name")} className="rw-input" data-testid="rt-form-name" />
                </Field>
                <Field label="Broj identičnih jedinica *">
                  <input type="number" min={1} max={200} required value={form.count_of_rooms} onChange={set("count_of_rooms")} className="rw-input" data-testid="rt-form-count" />
                  {!isNew && growth > 0 && (
                    <p className="mt-1 text-[11px] font-medium text-primary" data-testid="rt-form-growth-hint">
                      + {growth} nov{growth === 1 ? "a" : "e"} jedinic{growth === 1 ? "a" : "a"} će se dodati — upišite im{growth === 1 ? "e" : "ena"} ispod ↓
                    </p>
                  )}
                </Field>
                <Field label="Max cijena po noćenju (€) *" hint="Zakonski fiksna cijena. Cijene i rezervacije ne mogu biti veće; razlika ide na račun kao popust.">
                  <input type="number" min={0.01} step="0.01" required value={form.max_price ?? ""} onChange={set("max_price")} placeholder="npr. 200" className="rw-input" data-testid="rt-form-max-price" />
                </Field>
              </div>

              <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-foreground">
                💡 <strong>Cijene se postavljaju u Pricing stranici</strong> — kreiraj periode (npr. "Ljeto 2026") sa cijenama. Datumi bez perioda nisu dostupni za rezervaciju.
              </div>

              {/* Bed-space occupancy section */}
              <div className="rounded-md border border-border bg-muted/20 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Users size={14} weight="duotone" className="text-primary" />
                  <h5 className="text-sm font-semibold">Kapacitet (bed-space model)</h5>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">
                  <strong>Odrasla mjesta</strong> = kreveti gdje mogu spavati odrasli <em>ili</em> djeca.
                  <strong className="ml-2">Dječja mjesta</strong> = kreveti samo za djecu (npr. gornji krevet u bunk krevetu).
                  <strong className="ml-2">Bebe</strong> = dječji krevetići za novorođenčad — <u>nisu uračunati</u> u ukupan broj gostiju.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Odrasla mjesta *" hint="npr. 2 = bračni krevet za 2">
                    <input type="number" min={1} required value={form.adult_spaces} onChange={set("adult_spaces")} className="rw-input" data-testid="rt-form-adult-spaces" />
                  </Field>
                  <Field label="Dječja mjesta" hint="samo za djecu, npr. dječji bunk">
                    <input type="number" min={0} value={form.children_spaces} onChange={set("children_spaces")} className="rw-input" data-testid="rt-form-children-spaces" />
                  </Field>
                  <Field label="Krevetića za bebe" hint={`0–${infantAgeMax} god.`}>
                    <input type="number" min={0} value={form.cot_spaces} onChange={set("cot_spaces")} className="rw-input" data-testid="rt-form-cot-spaces" />
                  </Field>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Max ukupno gostiju" hint="hard cap za odrasle + djecu (auto-računato)">
                    <input type="number" min={1} value={form.max_occupancy} onChange={set("max_occupancy")} className="rw-input" data-testid="rt-form-max-occupancy" />
                  </Field>
                  <Field label="Standardno gostiju" hint="broj za koji se računa bazna cijena">
                    <input type="number" min={1} max={form.max_occupancy || 20} value={form.default_occupancy} onChange={set("default_occupancy")} className="rw-input" data-testid="rt-form-default-occupancy" />
                  </Field>
                </div>
              </div>

              <Field label="Tip smještaja">
                <select value={form.room_kind} onChange={set("room_kind")} className="rw-input">
                  <option value="room">Soba</option>
                  <option value="apartment">Apartman</option>
                  <option value="studio">Studio</option>
                  <option value="suite">Suite / Junior suite</option>
                  <option value="villa">Vila</option>
                  <option value="dorm">Dormitory</option>
                  <option value="bungalow">Kućica / Bungalov</option>
                  <option value="mobile_home">Mobile home</option>
                  <option value="camping">Kamp mjesto</option>
                </select>
              </Field>

              <Field label="Opis (opcionalno) — samo Booking Engine API, ne ide na OTA">
                <textarea rows={2} value={form.description || ""} onChange={set("description")} className="rw-input" />
              </Field>

              {/* Rename existing units — always visible when editing an
                  existing RoomType. Lets the user fix per-unit names later
                  (they previously could only set them at creation time). */}
              {!isNew && existingUnits.length > 0 && (
                <div className="rounded-md border border-border bg-muted/10 p-4" data-testid="rt-form-existing-units">
                  <div className="mb-2 flex items-center gap-2">
                    <Bed size={14} weight="duotone" className="text-primary" />
                    <h5 className="text-sm font-semibold">Postojeće jedinice ({existingUnits.length})</h5>
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Uredi ime svake jedinice — spremanje šalje samo one koje su stvarno promijenjene.
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {existingUnits.map((u) => {
                      const currentEdit = (form._unit_edits || []).find((e) => e.id === u.id);
                      const value = currentEdit ? currentEdit.name : _unitDisplayName(u);
                      return (
                        <div key={u.id} className="flex items-center gap-1.5">
                          <span className="w-14 shrink-0 rounded bg-muted px-1.5 py-1 text-center text-[10px] font-mono text-muted-foreground">{u.code || "—"}</span>
                          <input
                            value={value}
                            onChange={(ev) => {
                              const nv = ev.target.value;
                              setForm((f) => {
                                const rest = (f._unit_edits || []).filter((e) => e.id !== u.id);
                                return { ...f, _unit_edits: [...rest, { id: u.id, name: nv }] };
                              });
                            }}
                            placeholder={`Ime jedinice`}
                            className="rw-input flex-1"
                            data-testid={`rt-form-existing-unit-${u.id}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manual unit naming — for NEW room types OR when editing and count_of_rooms grew. */}
              {growth > 0 && (
                <div className="rounded-md border border-border bg-muted/10 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Bed size={14} weight="duotone" className="text-primary" />
                    <h5 className="text-sm font-semibold">
                      {isNew ? "Nazivi pojedinačnih jedinica" : `Nazivi ${growth} nov${growth === 1 ? "e" : "ih"} jedinic${growth === 1 ? "e" : "a"}`}
                    </h5>
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">
                    {isNew
                      ? <>Upišite ime svake jedinice ručno (npr. &quot;Soba 101&quot;, &quot;Apartman Bura&quot;, &quot;Studio A&quot;). Ostavite prazno ako želite auto-generirati imena.</>
                      : <>Postojeće jedinice zadržavaju svoje ime. Upišite ime za svaku novu jedinicu ili ostavite prazno za auto-generirano ime — kasnije ga možete promijeniti u <strong>Jedinice</strong>.</>}
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {Array.from({ length: growth }).map((_, i) => (
                      <input
                        key={i}
                        value={(form.unit_names && form.unit_names[i]) || ""}
                        onChange={setUnitName(i)}
                        placeholder={isNew ? `Naziv jedinice #${i + 1}` : `Nova jedinica #${(Number(originalCount) || 0) + i + 1}`}
                        className="rw-input"
                        data-testid={`rt-form-unit-name-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* --- Right: live scenario preview --- */}
            <aside className="space-y-3 lg:col-span-2">
              <div className="rounded-md border border-border bg-background p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Info size={12} weight="duotone" /> Kako će OTA-e prikazati sobu
                </div>
                <p className="text-sm">
                  Ova soba će se pojaviti u pretragama za <span className="font-bold text-primary">{totalMax || 0} osob{plural(totalMax)}</span>.
                </p>
                <div className="mt-3 space-y-1.5">
                  {scenarios.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Postavi broj kreveta da vidiš scenarije.</p>
                  ) : (
                    scenarios.map((s, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-xs">
                        <span className="text-primary">•</span>
                        <span>{s}</span>
                      </div>
                    ))
                  )}
                </div>
                {form.cot_spaces > 0 && (
                  <p className="mt-3 rounded bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
                    Bebe ({form.cot_spaces}× krevetić) <em>nisu</em> uračunate u ukupan broj gostiju — spavaju u vlastitim krevetićima.
                  </p>
                )}
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Baby size={12} weight="duotone" /> Dob (Objekt-postavka)
                </div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li><strong className="text-foreground">Beba:</strong> 0 – {infantAgeMax} god.</li>
                  <li><strong className="text-foreground">Dijete:</strong> {childAgeMin} – {childAgeMax} god.</li>
                  <li><strong className="text-foreground">Odrasli:</strong> {childAgeMax + 1}+ god.</li>
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Ova pravila vrijede za sve tipove smještaja u objektu. Promijeni ih u Objekti → Uredi objekt.
                </p>
              </div>
            </aside>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border bg-card px-6 py-4">
            <button type="button" onClick={onCancel} className="rounded-md border border-border bg-background px-4 py-1.5 text-sm font-medium hover:bg-muted">Odustani</button>
            <button type="submit" disabled={saving} className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="rt-form-save">
              {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="duotone" />}
              Spremi
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/**
 * Build the Booking.com-style "This room will appear in searches for..." list.
 * Rule (bed-space model):
 *   - adults can sleep in adult beds only
 *   - children can sleep in adult beds OR children beds
 *   - total (adults+children) ≤ max_occupancy
 *   - infants ignored (they have their own cots)
 */
function buildOccupancyScenarios({ adult_spaces, children_spaces, max_occupancy }) {
  if (adult_spaces < 1) return [];
  const scenarios = [];
  const cap = Math.max(1, max_occupancy || (adult_spaces + children_spaces));
  // Iterate over combinations, prefer higher adult counts first (top of Booking preview)
  for (let a = adult_spaces; a >= 0; a--) {
    for (let c = 0; c <= children_spaces + (adult_spaces - a); c++) {
      const total = a + c;
      if (total < 1 || total > cap) continue;
      if (a === 0) continue; // must have at least 1 adult per Channex rules
      // Ensure children fit either in children_spaces OR in the leftover adult beds
      const childrenInAdultBeds = Math.max(0, c - children_spaces);
      if (childrenInAdultBeds > adult_spaces - a) continue;
      scenarios.push(formatScenario(a, c));
    }
  }
  // dedupe + cap at 8 entries
  return Array.from(new Set(scenarios)).slice(0, 8);
}

function formatScenario(a, c) {
  const adultsLabel = `${a} ${a === 1 ? "odrasla osoba" : a < 5 ? "odrasle osobe" : "odraslih osoba"}`;
  if (c === 0) return adultsLabel;
  const childrenLabel = c === 1 ? "1 dijete" : `${c} djec${c < 5 ? "e" : "e"}`;
  return `${adultsLabel} + ${childrenLabel}`;
}

function plural(n) {
  return n === 1 ? "a" : "e";
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

/**
 * "Sync now" — manually triggers a full repair sync across the whole tenant.
 * Useful when auto-sync missed something (usually just after a Channex outage
 * or a fresh property import). Under the hood it calls the same idempotent
 * `POST /api/channels/channex/repair-tenant` endpoint used by our support
 * scripts, then shows a compact report to the user.
 */
function SyncNowButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const runSync = async () => {
    if (running) return;
    setRunning(true);
    const toastId = toast.loading("Sinkroniziram s kanalima…");
    try {
      const { data } = await api.post("/channels/channex/repair-tenant");
      const s = data.summary || {};
      const parts = [];
      if (s.properties_provisioned) parts.push(`${s.properties_provisioned} objekt${s.properties_provisioned === 1 ? "" : "a"} kreirano`);
      if (s.room_types_created) parts.push(`${s.room_types_created} novih tipova`);
      if (s.room_types_updated) parts.push(`${s.room_types_updated} ažuriranih`);
      if (s.room_types_recreated_after_orphan) parts.push(`${s.room_types_recreated_after_orphan} obnovljenih`);
      if (s.units_stamped) parts.push(`${s.units_stamped} jedinica`);
      const summary = parts.length ? parts.join(" · ") : "Sve već sinkronizirano";
      toast.success(`Sinkronizacija završena: ${summary}`, { id: toastId, duration: 6000 });
      if ((data.errors || []).length) {
        toast.warning(`${data.errors.length} greš${data.errors.length === 1 ? "ka" : "aka"} — provjeri detalje u konzoli.`, { duration: 6000 });
        console.warn("Channex repair errors:", data.errors);
      }
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err), { id: toastId });
    } finally {
      setRunning(false);
    }
  };
  return (
    <button
      onClick={runSync}
      disabled={running}
      title="Ponovno provjeri i pošalji sve u OTA"
      className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
      data-testid="rt-sync-now-btn"
    >
      <ArrowsClockwise size={14} weight="duotone" className={running ? "animate-spin" : ""} />
      {running ? "Sinkroniziram…" : "Sync"}
    </button>
  );
}
